import { CategoryOutlined, Close, Delete, Save } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import api from "../../../services/api";
import AlertModal from "../../alertModal";
import { AreaRow, ModalEditMassPolygonProps } from "./types";
import { validateAreaRow } from "./validation";
import PolygonsTable, { ShapeElementsTableHandle } from "./polygonsTable";

const styles = {
  modal: { display: "flex", justifyContent: "center", alignItems: "center" },
  paper: {
    maxWidth: "884px",
    width: "100%",
    height: "70vh",
    maxHeight: "90vh",
    minHeight: "632px",
    gap: 2,
    flexDirection: "column",
    display: "flex",
    alignItems: "flex-start",
    overflow: "hidden",
  },
  dialogTitle: {
    width: "100%",
  },
  boxHeader: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    mb: 1,
    p: 2,
  },
  boxTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignItems: "center",
    color: "primary.main",
    width: "100%",
    gap: 1,
  },
  dialogContent: {
    width: "100%",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: 2,
    gap: 1.5,
  },
  boxFooter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: 2,
    paddingTop: 0,
  },
};
export const ModalEditMassPolygon: FC<ModalEditMassPolygonProps> = ({
  open,
  onClose,
  mapId,
  elements = [],
  setSnackbar,
  onSaveComplete,
}: ModalEditMassPolygonProps) => {
  const theme = useTheme();
  const tableRef = useRef<ShapeElementsTableHandle>(null);

  const [areaData, setAreaData] = useState<AreaRow[]>([]);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const elementsRef = useRef(elements);
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);

  useEffect(() => {
    if (!open) {
      setAreaData([]);
      setConfirmSave(false);
      setConfirmDelete(false);
      setSaving(false);
      setDeleting(false);
      return;
    }
    setAreaData(elementsRef.current || []);
  }, [open]);

  const getRows = (): AreaRow[] =>
    (tableRef.current?.getRows() as AreaRow[]) ?? areaData;

  const checkedCount = areaData.filter((row) => row.checked).length;
  const hasErrors = areaData.some(
    (row) => Object.keys(validateAreaRow(row)).length > 0,
  );

  const handleValidateAndConfirmSave = () => {
    const rows = getRows();
    let hasErr = false;
    rows.forEach((row) => {
      row._errors = validateAreaRow(row);
      if (Object.keys(row._errors).length > 0) hasErr = true;
    });
    setAreaData([...rows]);
    if (hasErr) return;
    setConfirmSave(true);
  };

  const confirmSaveEdit = async () => {
    setConfirmSave(false);
    setSaving(true);
    const rows = getRows();
    const elementsData = rows.map((row) => ({
      id: row.elementId,
      titulo: row.titulo || "",
      tipo: 4,
      font: row.font ?? null,
      img: row.img ?? null,
      mapRefId: row.mapRefId ?? null,
      tags: row.tags ?? [],
      configuracao: {
        ...(row._origConfig ?? {}),
        polygon: {
          ...(row._origConfig?.polygon ?? {}),
          color: row.color,
          fillColor: row.fillColor ?? "",
        },
      },
    }));

    try {
      await api.put("/easyMaps/updateManyElements", { elementsData });
      setSnackbar?.({
        children: "Polígonos atualizados com sucesso!",
        severity: "success",
      });
      onSaveComplete?.();
      onClose();
    } catch (error) {
      console.error("Erro ao atualizar polígonos:", error);
      setSnackbar?.({
        children: "Erro ao atualizar polígonos.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    setConfirmDelete(false);
    const rows = getRows();
    const ids = rows
      .filter((row) => row.checked)
      .map((row) => row.elementId)
      .filter(Boolean);
    if (ids.length === 0) return;

    setDeleting(true);
    try {
      let logs: any = null;
      try {
        logs = JSON.parse(localStorage.getItem("logs") || "null");
      } catch {
        logs = null;
      }
      await api.delete("/easyMaps/deleteManyElements", {
        data: { ids, logsLoginClienteId: logs?.id },
      });

      setAreaData(rows.filter((row) => !row.checked));
      setSnackbar?.({
        children: `${ids.length} polígono(s) excluído(s) com sucesso!`,
        severity: "success",
      });
      onSaveComplete?.();
      onClose();
    } catch (error) {
      console.error("Erro ao excluir polígonos:", error);
      setSnackbar?.({
        children: "Erro ao excluir polígonos.",
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => onClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={styles.modal}
      >
        <Paper sx={styles.paper}>
          <DialogTitle sx={styles.dialogTitle} color="primary.main">
            <Box sx={styles.boxHeader}>
              <Box sx={styles.boxTitle}>
                <Stack
                  sx={{
                    height: "45px",
                    width: "45px",
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                    borderRadius: "5px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CategoryOutlined
                    fontSize="large"
                    sx={{ color: "primary.main" }}
                  />
                </Stack>
                <Stack
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: "260px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    fontWeight={500}
                  >
                    Polígonos
                  </Typography>{" "}
                  <Typography
                    sx={{
                      minWidth: "260px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    fontSize={14}
                    color="text.secondary"
                  >
                    Edição em massa de polígonos
                  </Typography>
                </Stack>
              </Box>
              <IconButton
                aria-label="close"
                onClick={() => {
                  onClose();
                }}
              >
                <Close />
              </IconButton>
            </Box>
            <Divider />
          </DialogTitle>
          <DialogContent sx={styles.dialogContent}>
            <PolygonsTable
              ref={tableRef}
              kind="area"
              data={areaData}
              onDataChange={setAreaData}
            />
          </DialogContent>
          <Box sx={styles.boxFooter}>
            {checkedCount > 0 ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Remover ({checkedCount})
              </Button>
            ) : (
              <Box />
            )}
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleValidateAndConfirmSave}
              disabled={saving || areaData.length === 0 || hasErrors}
              size="small"
              sx={{ textTransform: "none" }}
            >
              Salvar alterações
            </Button>
          </Box>
        </Paper>
      </Modal>

      <AlertModal
        openModal={confirmSave}
        setOpenModal={setConfirmSave}
        singleButton={false}
        severity="info"
        textContent="Deseja confirmar a edição dos polígonos?"
        buttonText="Confirmar"
        buttonCancelText="Cancelar"
        handleButton={confirmSaveEdit}
      />

      <AlertModal
        openModal={confirmDelete}
        setOpenModal={setConfirmDelete}
        singleButton={false}
        severity="warning"
        textContent={`Deseja excluir os ${checkedCount} polígono(s) selecionado(s)? Esta ação não pode ser desfeita.`}
        buttonText="Excluir"
        buttonCancelText="Cancelar"
        handleButton={handleDeleteSelected}
      />
    </>
  );
};
